/**
 * Print carousels (sections/print-carousel.liquid): every row of vintage
 * prints on the store is this one section, set by where its prints come
 * from (data-print-carousel), each source an adapter of its own:
 *
 *   media      one set of a product's photos and videos, from the Hammies
 *              media catalog (assets/print-carousel-media.js)
 *   stories    a product's Inspiration and History, as magazine pages or
 *              prints of their photos (assets/print-carousel-stories.js)
 *   instagram  the store's latest Instagram posts
 *              (assets/print-carousel-instagram.js)
 *   collection the photos featured for a collection, from the same
 *              catalog (assets/print-carousel-collection.js)
 *
 * and laid out by one of three engines (data-layout): a row of prints
 * (assets/print-row.js), a magazine that turns as it is swiped across
 * (assets/print-magazine.js), or a wall of framed posters
 * (assets/poster-wall.js, a collection page's hero). All take the same items,
 * so any source suits any of them.
 *
 * This is all a page loads up front. Once a carousel comes within a screen
 * of view, its engine and its source's adapter are fetched, each once
 * however many carousels want it. The adapter hands its loader here
 * (PrintCarousel.source), and this registers it with the engine for that
 * source and layout, which fills the carousel: a page never scrolled that
 * far never loads either.
 */
(() => {
  if (window.PrintCarousel) return;

  const SELECTOR = "[data-print-carousel]";
  // Each layout's engine, by the name it sets on window.
  const ENGINES = {
    row: "PrintRow",
    magazine: "PrintMagazine",
    wall: "PosterWall"
  };

  // Each source's loader, as its adapter hands it over.
  const sources = new Map();
  window.PrintCarousel = {
    source: (name, loader) => sources.set(name, loader)
  };

  // Each script once, by its URL.
  const scripts = new Map();
  const loadScript = (src) => {
    if (!scripts.has(src))
      scripts.set(
        src,
        new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = src;
          script.onload = resolve;
          script.onerror = () => reject(new Error(`${src} did not load`));
          document.head.append(script);
        })
      );
    return scripts.get(src);
  };

  const near = new IntersectionObserver(
    (entries) => {
      entries.forEach(async (entry) => {
        if (!entry.isIntersecting) return;
        near.unobserve(entry.target);
        const {
          printCarousel: source,
          layout = "row",
          engineSrc,
          adapterSrc
        } = entry.target.dataset;
        try {
          await Promise.all([loadScript(engineSrc), loadScript(adapterSrc)]);
          const engine = window[ENGINES[layout]];
          const loader = sources.get(source);
          if (!engine || !loader)
            throw new Error(`no ${layout} engine or ${source} source`);
          // Registering again is harmless: an engine loads each carousel once.
          engine.register(
            `[data-print-carousel="${source}"][data-layout="${layout}"]`,
            loader
          );
        } catch (error) {
          entry.target.hidden = true;
          console.warn("[print-carousel] could not load the row:", error);
        }
      });
    },
    { rootMargin: "100% 0px" }
  );

  const watch = (root) =>
    (root.matches?.(SELECTOR)
      ? [root]
      : root.querySelectorAll(SELECTOR)
    ).forEach((container) => near.observe(container));
  watch(document);
  // The theme editor renders a section in place, perhaps one from a source
  // whose adapter is not loaded yet; the engine picks up the rest itself.
  document.addEventListener("shopify:section:load", (event) =>
    watch(event.target)
  );
})();
